const VALIDATION = {
  MAX_PDF_SIZE: 10 * 1024 * 1024,
  MIN_FILE_SIZE: 0,
  PDF_HEADER: "%PDF",
  PDF_HEADER_CHECK_LENGTH: 16,
  DEFAULT_MIME_TYPE: "application/octet-stream",
};

function safeString(value, fallback = "") {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    try {
      return String(value);
    } catch {
      return fallback;
    }
  }
}

function safeLength(value) {
  return safeString(value, "").length;
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    } catch (err) {
      reject(err);
    }
  });
}

function extractBase64FromDataURL(dataURL) {
  if (!dataURL || typeof dataURL !== "string") return "";
  const parts = dataURL.split(",");
  return parts[1] || "";
}

async function fileToBase64(file) {
  try {
    const dataURL = await readFileAsDataURL(file);
    const base64 = extractBase64FromDataURL(dataURL);

    return base64;
  } catch (error) {
    return "";
  }
}

function validatePDFHeader(base64) {
  if (typeof base64 !== "string" || base64.length === 0) return false;
  try {
    const checkLen = Math.min(VALIDATION.PDF_HEADER_CHECK_LENGTH, Math.max(4, base64.length));
    const binaryString = atob(base64.substring(0, checkLen));
    return typeof binaryString === "string" && binaryString.startsWith(VALIDATION.PDF_HEADER);
  } catch (err) {
    return false;
  }
}

async function pdfToBase64(file) {
  try {
    const dataURL = await readFileAsDataURL(file);
    const base64 = extractBase64FromDataURL(dataURL);

    if (!validatePDFHeader(base64)) {
      return "";
    }

    return base64;
  } catch (error) {
    return "";
  }
}

function isPDFTooLarge(file) {
  return file?.size > VALIDATION.MAX_PDF_SIZE;
}

function validateSingleFile(file) {

  if (isPDFTooLarge(file)) {
    return false;
  }

  return true;
}

function separateValidAndInvalidFiles(files) {
  const validFiles = [];
  const invalidFiles = [];

  for (const file of files) {
    try {
      if (validateSingleFile(file)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    } catch (err) {
      invalidFiles.push(file);
    }
  }

  return { validFiles, invalidFiles };
}

function validateFiles(files) {
  const normalizedFiles = Array.isArray(files) ? files : [];
  const { validFiles } = separateValidAndInvalidFiles(normalizedFiles);

  return validFiles;
}

async function processFileInstance(file) {
  if (safeString(file?.type) === "application/pdf") {
    return await pdfToBase64(file);
  } else {
    return await fileToBase64(file);
  }
}

function processPreEncodedFile(file) {
  return typeof file.data === "string" ? file.data : safeString(file.data, "");
}

function validateFileStructure(file) {
  const isFileInstance = typeof File !== "undefined" && file instanceof File;
  const hasData = file && (typeof file.data === "string" || typeof file.data === "object");

  if (!isFileInstance && !hasData) {
    return false;
  }

  return true;
}

function createAttachmentObject(base64, file) {
  const mime = safeString(file?.type, VALIDATION.DEFAULT_MIME_TYPE);
  const b64 = typeof base64 === "string" ? base64 : safeString(base64, "");

  const attachment = {
    base64: b64,
    mime: mime,
  };

  return attachment;
}

async function convertFileToAttachment(file) {
  if (!validateFileStructure(file)) {
    return null;
  }

  let base64;
  if (typeof File !== "undefined" && file instanceof File) {
    base64 = await processFileInstance(file);
  } else {
    base64 = processPreEncodedFile(file);
  }

  return createAttachmentObject(base64, file);
}

function isInvalidSessionId(sessionId) {
  const s = safeString(sessionId, "");
  return s === "null" || s === "undefined" || s === "";
}

function normalizeSessionId(sessionId) {
  return isInvalidSessionId(sessionId) ? "" : safeString(sessionId, "");
}

const RequestBuilderUtil = {
  VALIDATION,
  safeString,
  safeLength,
  readFileAsDataURL,
  extractBase64FromDataURL,
  fileToBase64,
  validatePDFHeader,
  pdfToBase64,
  isPDFTooLarge,
  validateSingleFile,
  separateValidAndInvalidFiles,
  validateFiles,
  processFileInstance,
  processPreEncodedFile,
  validateFileStructure,
  createAttachmentObject,
  convertFileToAttachment,
  isInvalidSessionId,
  normalizeSessionId,
};

export default RequestBuilderUtil;
